// Accounts tab: pick an operator account, see its effective permissions
// (role ∪ individual grants, with source badges), add/remove individual grants,
// and change its role.
import { useEffect, useState } from 'react';
import { Operator, Role, PermissionDef, AdminOperator } from '../../types/database';
import { adminAccessService } from '../../services/adminAccess';
import PermissionMatrix from './PermissionMatrix';

interface AccountsPanelProps {
  actor: Operator;
  operators: AdminOperator[];
  roles: Role[];
  permissions: PermissionDef[];
  /** role key → set of permission keys granted by that role. */
  rolePermsByRole: Map<string, Set<string>>;
  onChanged: () => Promise<void> | void;
  onError: (message: string) => void;
}

function operatorLabel(o: AdminOperator): string {
  if (o.name && o.name.trim()) return o.name.trim();
  return o.operator_code ? `#${o.operator_code}` : o.email || o.id.slice(0, 8);
}

function operatorSubtitle(o: AdminOperator): string {
  return (
    [o.operator_code ? `#${o.operator_code}` : null, o.department, o.email]
      .filter(Boolean)
      .join(' · ') || '—'
  );
}

function roleName(roles: Role[], key: string): string {
  return roles.find((r) => r.key === key)?.name ?? key;
}

export default function AccountsPanel({
  actor,
  operators,
  roles,
  permissions,
  rolePermsByRole,
  onChanged,
  onError,
}: AccountsPanelProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [savingRole, setSavingRole] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);

  const selected = operators.find((o) => o.id === selectedId) ?? null;

  // Reset the name editor when the selected account (or its saved name) changes.
  useEffect(() => {
    setNameDraft(selected?.name ?? '');
  }, [selectedId, selected?.name]);

  const term = search.trim().toLowerCase();
  const filtered = term
    ? operators.filter((o) =>
        [o.name, o.operator_code, o.email, o.department, o.role]
          .filter(Boolean)
          .some((v) => (v as string).toLowerCase().includes(term))
      )
    : operators;

  const individualKeys = selected
    ? new Set(Object.entries(selected.permissions).filter(([, v]) => v === true).map(([k]) => k))
    : new Set<string>();
  const roleKeys = selected ? rolePermsByRole.get(selected.role) ?? new Set<string>() : new Set<string>();

  async function run(fn: () => Promise<unknown>, key?: string) {
    if (key) setBusyKey(key);
    try {
      await fn();
      await onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      if (key) setBusyKey(null);
    }
  }

  async function handleSaveName() {
    if (!selected) return;
    setSavingName(true);
    try {
      await adminAccessService.setOperatorName(actor, selected.id, nameDraft.trim());
      await onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingName(false);
    }
  }

  async function handleRoleChange(newRole: string) {
    if (!selected || newRole === selected.role) return;
    if (!confirm(`Change ${operatorLabel(selected)} from "${roleName(roles, selected.role)}" to "${roleName(roles, newRole)}"?`)) return;
    setSavingRole(true);
    try {
      await adminAccessService.setOperatorRole(actor, selected.id, newRole);
      await onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingRole(false);
    }
  }

  return (
    <div className="access-split">
      <div className="access-list">
        <input
          className="access-list-search"
          placeholder="Search code, email, department…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="access-list-items">
          {filtered.map((o) => (
            <button
              key={o.id}
              className={`access-list-item ${o.id === selectedId ? 'selected' : ''}`}
              onClick={() => setSelectedId(o.id)}
            >
              <span className="access-list-item-main">
                <span className="access-list-item-title">
                  {operatorLabel(o)} {!o.is_active && '(inactive)'}
                </span>
                <span className="access-list-item-sub">{operatorSubtitle(o)}</span>
              </span>
              <span className="access-badge access-badge--count">{roleName(roles, o.role)}</span>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="access-detail-empty" style={{ padding: '1rem' }}>No accounts.</div>
          )}
        </div>
      </div>

      <div className="access-detail">
        {!selected ? (
          <div className="access-detail-empty">Select an account to manage its permissions.</div>
        ) : (
          <>
            <div className="access-detail-header">
              <div>
                <h2>{operatorLabel(selected)}</h2>
                <div className="access-detail-sub">{operatorSubtitle(selected)}</div>
              </div>
            </div>

            <div className="access-field">
              <label htmlFor="account-name">Name</label>
              <div className="access-row">
                <input
                  id="account-name"
                  type="text"
                  value={nameDraft}
                  placeholder="e.g. Jan de Vries"
                  style={{ flex: 1 }}
                  onChange={(e) => setNameDraft(e.target.value)}
                />
                <button
                  className="access-btn"
                  disabled={savingName || nameDraft.trim() === (selected.name ?? '').trim()}
                  onClick={handleSaveName}
                >
                  Save
                </button>
              </div>
            </div>

            <div className="access-field">
              <label htmlFor="account-role">Role</label>
              <select
                id="account-role"
                value={selected.role}
                disabled={savingRole || selected.id === actor.id}
                onChange={(e) => handleRoleChange(e.target.value)}
              >
                {roles.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.name}
                  </option>
                ))}
              </select>
              {selected.id === actor.id && (
                <div className="access-note">You can’t change your own role (anti-lockout). Have another admin do it.</div>
              )}
            </div>

            <div className="access-field" style={{ marginBottom: 0 }}>
              <label>Permissions (role + individual)</label>
              <div className="access-note">
                <span className="access-badge access-badge--role">Role</span> inherited from{' '}
                {roleName(roles, selected.role)} (change on the Roles tab).{' '}
                <span className="access-badge access-badge--individual">Individual</span> granted on this account only.
              </div>
            </div>
            <PermissionMatrix
              mode="account"
              permissions={permissions}
              rolePermissionKeys={roleKeys}
              individualKeys={individualKeys}
              busyKey={busyKey}
              onToggleIndividual={(key, enabled) =>
                run(() => adminAccessService.setOperatorPermission(actor, selected.id, key, enabled), key)
              }
            />
          </>
        )}
      </div>
    </div>
  );
}
